import {
  playbackFrame,
  playbackPlan,
  playbackStepEnd,
  playbackTrails,
  type DiagramElement,
  type PlaybackFrame,
  type TrainingDiagram,
} from '@ukinfo/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Select, cn } from '../ui';
import {
  ARROW,
  LAYER,
  PITCH_HEIGHT,
  PitchSurface,
  SWITCH_COLOR,
  SketchElement,
  StepNumbers,
  arrowGuidePath,
  arrowScale,
  describeDiagram,
  drawingGeometry,
  pitchViewBox,
  useMarkerId,
  zoneColorsOf,
} from './PitchDiagram';
import type { DiagramViewport } from './useDiagramViewport';

/**
 * Skizze mit Ablauf: Die Pfeile werden Schritt fuer Schritt abgespielt, Spieler
 * und Ball laufen entlang der Pfeile. Zeichnet mit denselben Bausteinen wie
 * `PitchDiagram`; die Rechnung steckt in `playbackFrame` (Shared-Paket).
 *
 * Unter der Skizze liegen die Knoepfe: Abspielen/Pause, Anfang, Schritt zurueck
 * und vor, Tempo. Bei reduzierter Bewegung (prefers-reduced-motion) laeuft
 * nichts von selbst - der Ablauf springt dann mit "Schritt vor" und "Schritt
 * zurueck" von Stand zu Stand. Skizzen ohne Pfeile bleiben ohne Leiste.
 *
 * Die Auswahl "Pfeile" bestimmt, was von den Pfeilen zu sehen ist: alle (gelaufene blass, der aktive
 * hervorgehoben), nur der aktuelle Schritt, nur die kommenden, statt der Pfeile die zurueckgelegten
 * Wege als duenne Spur, oder keine. Masslinien bleiben immer. Die Wahl merkt sich das Geraet
 * (localStorage; ohne Speicher gilt "Alle"). Die statische Anzeige und der Druck kennen nur "Alle".
 *
 * In der Vollbildansicht (`fullscreen`) fuellt die Skizze den Platz, im Querformat
 * steht die Leiste daneben. Dort zoomt und schwenkt `viewport` (siehe
 * `useDiagramViewport`); sonst ist die Skizze fest.
 */

const SPEEDS = [0.5, 1, 2] as const;
type Speed = (typeof SPEEDS)[number];
/** Laengste Zeit, die ein Bild zaehlt - nach einem Tabwechsel springt der Ablauf nicht weit. */
const MAX_TICK = 0.1;
/** Toleranz beim Vergleichen von Zeitpunkten. */
const EPSILON = 1e-6;

const SPEED_LABELS: Record<Speed, string> = { 0.5: '0,5×', 1: '1×', 2: '2×' };

/** Was von den Pfeilen zu sehen ist. */
export const ARROW_MODES = ['all', 'current', 'upcoming', 'trace', 'none'] as const;
export type ArrowMode = (typeof ARROW_MODES)[number];

export const ARROW_MODE_LABELS: Record<ArrowMode, string> = {
  all: 'Alle',
  current: 'Nur aktueller Schritt',
  upcoming: 'Nur kommende',
  trace: 'Spur',
  none: 'Keine',
};

/** Schluessel im localStorage: die Wahl gilt je Geraet. */
export const ARROW_MODE_KEY = 'ukinfo.skizze.pfeile';

function readArrowMode(): ArrowMode {
  try {
    const stored = window.localStorage.getItem(ARROW_MODE_KEY);
    return ARROW_MODES.find((mode) => mode === stored) ?? 'all';
  } catch {
    // Ohne Speicher (privater Modus, gesperrt) gilt die Voreinstellung.
    return 'all';
  }
}

function saveArrowMode(mode: ArrowMode): void {
  try {
    window.localStorage.setItem(ARROW_MODE_KEY, mode);
  } catch {
    // Nicht merken ist kein Fehler.
  }
}

/** Folgt der Systemeinstellung "Bewegung reduzieren". */
function useReducedMotion(): boolean {
  const query = (): MediaQueryList | null => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null);
  const [reduced, setReduced] = useState(() => query()?.matches ?? false);
  useEffect(() => {
    const media = query();
    if (!media) return;
    const update = (): void => setReduced(media.matches);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);
  return reduced;
}

/* ------------------------------------------------------------------ Symbole */

type IconId = 'play' | 'pause' | 'restart' | 'back' | 'forward' | 'replay';

function Icon({ id }: { id: IconId }): JSX.Element {
  const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" aria-hidden="true">
      {id === 'play' && <path d="M8 5l11 7-11 7z" fill="currentColor" />}
      {id === 'pause' && <path d="M8 5v14M16 5v14" {...stroke} strokeWidth={3} />}
      {id === 'restart' && <path d="M6 5v14M19 5l-9 7 9 7z" {...stroke} />}
      {id === 'back' && <path d="M15 5l-8 7 8 7" {...stroke} />}
      {id === 'forward' && <path d="M9 5l8 7-8 7" {...stroke} />}
      {id === 'replay' && <path d="M4 12a8 8 0 108-8H8M8 4L5 7l3 3" {...stroke} />}
    </svg>
  );
}

function ControlButton({
  label,
  text,
  icon,
  primary,
  disabled,
  onClick,
}: {
  label: string;
  text: string;
  icon: IconId;
  primary?: boolean;
  disabled?: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex min-h-12 min-w-0 flex-col items-center justify-center gap-0.5 rounded-xl border px-1 py-1 text-[0.7rem] font-medium leading-tight shadow-sm transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 disabled:cursor-not-allowed disabled:opacity-45',
        primary ? 'border-brand-600 bg-brand-600 text-white hover:bg-brand-700' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
      )}
    >
      <Icon id={icon} />
      <span className="max-w-full truncate">{text}</span>
    </button>
  );
}

/* ------------------------------------------------------------------ Zeichnung */

/** Ein Element der Skizze, gegebenenfalls an der Stelle, an der es der Ablauf gerade zeigt. */
function PlayerElement({
  element,
  index,
  frame,
  markerId,
  height,
  scale,
  shown,
}: {
  element: DiagramElement;
  index: number;
  frame: PlaybackFrame;
  markerId: string;
  height: number;
  scale: number;
  /** Pfeile: nach der Wahl "Pfeile" sichtbar oder ausgeblendet; andere Elemente zeigt der Player immer. */
  shown: boolean;
}): JSX.Element | null {
  if (element.type === 'player' || element.type === 'ball') {
    const at = frame.positions.get(index);
    if (!at) return null;
    // Spieler mit Blickrichtung drehen sich beim Laufen in Laufrichtung (der Ablauf liefert den Winkel); hoher Ball: Flughoehe.
    const facing = element.type === 'player' ? frame.facing.get(index) : undefined;
    const turned = element.type === 'player' && facing !== undefined ? { ...element, x: at.x, y: at.y, facing } : { ...element, x: at.x, y: at.y };
    return <SketchElement element={turned} markerId={markerId} height={height} scale={scale} lift={element.type === 'ball' ? frame.lift.get(index) : undefined} />;
  }
  if (element.type !== 'arrow') return <SketchElement element={element} markerId={markerId} height={height} scale={scale} />;
  if (!shown) return null;

  const state = frame.activeArrows.includes(index) ? 'active' : frame.doneArrows.includes(index) ? 'done' : 'upcoming';
  if (state === 'active') {
    // Der Positionswechsel leuchtet tuerkis und schwaecher - dezent anders als die weissen Schweife der anderen Pfeile.
    const glow =
      element.kind === 'SWITCH'
        ? ({ fill: 'none', stroke: SWITCH_COLOR, strokeWidth: 3, strokeLinecap: 'round', opacity: 0.4 } as const)
        : ({ fill: 'none', stroke: '#ffffff', strokeWidth: 3.4, strokeLinecap: 'round', opacity: 0.55 } as const);
    const y1 = (element.from[1] / 100) * height;
    const y2 = (element.to[1] / 100) * height;
    return (
      <g data-arrow-state="active" data-kind={element.kind}>
        {/* Der helle Schein folgt der Kurve und dem Linienzug; gerade Pfeile behalten ihre Linie. */}
        {element.via || element.points ? (
          <path d={arrowGuidePath(drawingGeometry(element, height))} {...glow} />
        ) : (
          <line x1={element.from[0]} y1={y1} x2={element.to[0]} y2={y2} {...glow} />
        )}
        <SketchElement element={element} markerId={markerId} height={height} scale={scale} />
      </g>
    );
  }
  return (
    <g data-arrow-state={state} opacity={state === 'done' ? 0.3 : 1}>
      <SketchElement element={element} markerId={markerId} height={height} scale={scale} />
    </g>
  );
}

/* --------------------------------------------------------------------- Player */

export function DiagramPlayer({
  diagram,
  className,
  title,
  viewport,
  fullscreen = false,
}: {
  diagram: TrainingDiagram;
  className?: string;
  title?: string;
  /** Zoom und Schwenken der Skizze (Vollbild); ohne ist sie fest. */
  viewport?: DiagramViewport;
  /** Fuellt den verfuegbaren Platz, die Leiste steht unten oder im Querformat rechts. */
  fullscreen?: boolean;
}): JSX.Element {
  const markerId = useMarkerId();
  const height = PITCH_HEIGHT[diagram.pitch];
  const scale = diagram.symbolScale ?? 1;
  const reduced = useReducedMotion();
  const plan = useMemo(() => playbackPlan(diagram), [diagram]);
  const duration = plan.duration;
  const hasArrows = plan.steps.length > 0;

  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<Speed>(1);
  const [arrowMode, setArrowModeState] = useState<ArrowMode>(readArrowMode);
  const timeRef = useRef(0);

  const goTo = (next: number): void => {
    timeRef.current = next;
    setTime(next);
  };

  // Eine andere Skizze beginnt wieder vorn.
  const signature = useMemo(() => JSON.stringify(diagram), [diagram]);
  useEffect(() => {
    timeRef.current = 0;
    setTime(0);
    setPlaying(false);
  }, [signature]);

  // Bei reduzierter Bewegung laeuft nichts von selbst.
  useEffect(() => {
    if (reduced) setPlaying(false);
  }, [reduced]);

  // Der Lauf: pro Bild rueckt die Zeit um die verstrichene Zeit mal Tempo vor.
  useEffect(() => {
    if (!playing) return;
    let frameId = 0;
    let last: number | null = null;
    const tick = (now: number): void => {
      const elapsed = last === null ? 0 : Math.min(Math.max((now - last) / 1000, 0), MAX_TICK);
      last = now;
      const next = Math.min(duration, timeRef.current + elapsed * speed);
      timeRef.current = next;
      setTime(next);
      if (next >= duration) {
        setPlaying(false);
        return;
      }
      frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [playing, speed, duration]);

  const frame = useMemo(() => playbackFrame(diagram, time), [diagram, time]);

  const setArrowMode = (mode: ArrowMode): void => {
    setArrowModeState(mode);
    saveArrowMode(mode);
  };
  // Pfeile des zuletzt begonnenen Schritts: "aktueller Schritt" zeigt sie auch in der Pause nach dem Schritt.
  // Ein Lauf ueber mehrere Schritte gehoert zu jedem Schritt, den er ueberspannt.
  const currentArrows =
    frame.step > 0 ? plan.steps.filter((step) => step.number <= frame.step).flatMap((step) => step.moves.filter((move) => move.endStep >= frame.step).map((move) => move.arrow)) : [];
  const arrowShown = (index: number): boolean => {
    switch (arrowMode) {
      case 'all':
        return true;
      case 'current':
        return currentArrows.includes(index);
      case 'upcoming':
        return !frame.doneArrows.includes(index);
      default:
        return false;
    }
  };
  const trails = useMemo(() => (arrowMode === 'trace' ? playbackTrails(diagram, time) : []), [arrowMode, diagram, time]);
  const arrowFactor = arrowScale(scale);

  const drawn = diagram.elements
    .map((element, index) => ({ element, index }))
    .sort((a, b) => LAYER[a.element.type] - LAYER[b.element.type]);
  // Baelle, die erst mit einem Pass erscheinen, haben Indizes hinter den Elementen der Skizze.
  const appearing = [...frame.positions.keys()].filter((index) => index >= diagram.elements.length);

  const atEnd = frame.time >= duration;
  const togglePlay = (): void => {
    if (playing) {
      setPlaying(false);
      return;
    }
    if (atEnd) goTo(0);
    setPlaying(true);
  };
  const stepBack = (): void => {
    setPlaying(false);
    goTo(playbackStepEnd(diagram, frame.step - 1));
  };
  const stepForward = (): void => {
    setPlaying(false);
    const next = plan.steps.find((step) => step.end > frame.time + EPSILON);
    goTo(next ? next.end : duration);
  };
  const restart = (): void => {
    setPlaying(false);
    goTo(0);
  };

  const status =
    frame.step === 0 ? `Start · ${frame.stepCount} ${frame.stepCount === 1 ? 'Schritt' : 'Schritte'}` : `Schritt ${frame.step} von ${frame.stepCount}`;

  return (
    <div data-testid="diagram-player" className={fullscreen ? 'flex h-full min-h-0 flex-col gap-2 landscape:flex-row' : 'space-y-2'}>
      {/* Im Vollbild liegt das SVG absolut im Platzhalter: es fuellt ihn, die Skizze passt sich ein (meet, zentriert). */}
      <div className={fullscreen ? 'relative min-h-0 min-w-0 flex-1' : undefined}>
        <svg
          ref={viewport?.svgRef}
          role="img"
          aria-label={title ? `${title}: ${describeDiagram(diagram)}` : describeDiagram(diagram)}
          viewBox={viewport?.viewBox ?? pitchViewBox(diagram.pitch)}
          className={fullscreen ? 'absolute inset-0 block h-full w-full select-none' : cn('block h-auto w-full', className)}
          data-pitch={diagram.pitch}
          data-step={frame.step}
          data-zoom={viewport?.zoomed ? viewport.zoom : undefined}
          style={{
            printColorAdjust: 'exact',
            WebkitPrintColorAdjust: 'exact',
            // Mit Zoom bedienen Finger und Maus die Skizze selbst: die Seite darf nicht scrollen oder zoomen.
            ...(viewport ? { touchAction: 'none', WebkitTouchCallout: 'none', WebkitUserSelect: 'none' } : {}),
          }}
          {...viewport?.panHandlers}
        >
          <PitchSurface pitch={diagram.pitch} markerId={markerId} scale={scale} zoneColors={zoneColorsOf(diagram.elements)} />
          {arrowMode === 'trace' && trails.length > 0 && (
            <g data-testid="arrow-trails" fill="none" strokeLinecap="round" strokeLinejoin="round" pointerEvents="none">
              {trails.map((trail) => (
                <path
                  key={`${trail.arrow}-${trail.entity}`}
                  data-kind={trail.kind}
                  d={`M ${trail.points.map((point) => `${point.x.toFixed(2)} ${((point.y / 100) * height).toFixed(2)}`).join(' L ')}`}
                  stroke={trail.kind === 'SWITCH' || trail.follow ? SWITCH_COLOR : ARROW}
                  strokeWidth={Math.round(0.55 * arrowFactor * 100) / 100}
                  opacity={0.9}
                />
              ))}
            </g>
          )}
          {drawn.map(({ element, index }) => (
            <PlayerElement key={index} element={element} index={index} frame={frame} markerId={markerId} height={height} scale={scale} shown={arrowShown(index)} />
          ))}
          {appearing.map((index) => {
            const at = frame.positions.get(index)!;
            return <SketchElement key={`ball-${index}`} element={{ type: 'ball', x: at.x, y: at.y }} markerId={markerId} height={height} scale={scale} lift={frame.lift.get(index)} />;
          })}
          {hasArrows && arrowMode !== 'none' && arrowMode !== 'trace' && <StepNumbers diagram={diagram} height={height} visible={arrowShown} />}
        </svg>
      </div>

      {hasArrows && (
        <div
          role="group"
          aria-label="Ablauf"
          data-testid="player-controls"
          className={cn('space-y-2 print:hidden', fullscreen ? 'shrink-0 rounded-xl bg-white p-2 landscape:w-72 landscape:overflow-y-auto' : 'mx-auto max-w-md')}
        >
          <div className={cn('grid gap-1.5', reduced ? 'grid-cols-3' : 'grid-cols-4')}>
            <ControlButton label="Zurück zum Anfang" text="Anfang" icon="restart" disabled={frame.time === 0} onClick={restart} />
            <ControlButton label="Schritt zurück" text="Zurück" icon="back" disabled={frame.time === 0} onClick={stepBack} />
            {reduced ? (
              <ControlButton label="Schritt vor" text="Weiter" icon="forward" primary disabled={atEnd} onClick={stepForward} />
            ) : (
              <ControlButton
                label={playing ? 'Pause' : atEnd ? 'Noch einmal abspielen' : 'Abspielen'}
                text={playing ? 'Pause' : atEnd ? 'Nochmal' : 'Abspielen'}
                icon={playing ? 'pause' : atEnd ? 'replay' : 'play'}
                primary
                onClick={togglePlay}
              />
            )}
            {!reduced && <ControlButton label="Schritt vor" text="Weiter" icon="forward" disabled={atEnd} onClick={stepForward} />}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
            <p role="status" aria-live="polite" data-testid="player-status" className="text-sm font-medium tabular-nums text-slate-700">
              {status}
            </p>
            {!reduced && (
              <div role="group" aria-label="Tempo" className="flex gap-1">
                {SPEEDS.map((value) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={speed === value}
                    aria-label={`Tempo ${SPEED_LABELS[value]}`}
                    onClick={() => setSpeed(value)}
                    className={cn(
                      'min-h-11 min-w-12 rounded-xl border px-3 text-sm font-medium shadow-sm transition-colors',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60',
                      speed === value ? 'border-brand-600 bg-brand-600 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
                    )}
                  >
                    {SPEED_LABELS[value]}
                  </button>
                ))}
              </div>
            )}
          </div>
          <label className="flex items-center justify-between gap-2 text-sm font-medium text-slate-700">
            Pfeile
            <Select
              data-testid="arrow-mode"
              value={arrowMode}
              onChange={(event) => setArrowMode(event.target.value as ArrowMode)}
              className="min-h-11 w-auto py-1.5 sm:min-h-0"
            >
              {ARROW_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {ARROW_MODE_LABELS[mode]}
                </option>
              ))}
            </Select>
          </label>
          {reduced && (
            <p className="text-xs text-slate-500">Bewegung ist in den Systemeinstellungen reduziert: Der Ablauf läuft nicht von selbst. Mit „Weiter“ geht es Schritt für Schritt.</p>
          )}
        </div>
      )}
    </div>
  );
}
